import { darken, lighten } from '@material-ui/core/styles/colorManipulator'
import isDarkColor from '@purecarslabs/signal-utils/isDarkColor'
import { MvhColorsType } from '@app-src/types'
import { MakeBrandType } from './types'

/**
 * cleanupBrandColor
 * @description Cleans up a color from the brand endpoint to use as a CSS color
 * @param {String} rawColor - the raw color obtained from the brand endpoint, which may be null
 * @return {String} - a cleaned up version of the color, or empty string if there was no color
 */
function cleanupBrandColor(rawColor?: string | null): string {
  if (!rawColor) {
    return ''
  }
  return rawColor.startsWith('#') ? rawColor : `#${rawColor}`
}

/**
 * blackOrWhiteText
 * @description Returns a good text color to lay on top of the given background color
 * @param {String} backgroundColor - a CSS color string for the background color
 * @return {String} - an appropriate color to use for text on top of that background
 */
function blackOrWhiteText(backgroundColor: string): string {
  if (!backgroundColor) {
    return ''
  }
  return isDarkColor(backgroundColor) ? '#fff' : '#000'
}

/**
 * transformMakeBrandIntoMvhColor
 * @description transforms the colors and fonts in a MakeBrandType object into the MvhColorsType (by inventing some colors)
 * @param {Object} makeBrand - the MakeBrandType object to convert
 * @return {Object} - the MvhColorsType object obtained
 */
export function transformMakeBrandIntoMvhColor({
  primaryColor,
  primaryFontUrl,
  secondaryColor,
  secondaryFontUrl
}: MakeBrandType): MvhColorsType {
  primaryColor = cleanupBrandColor(primaryColor)
  secondaryColor = cleanupBrandColor(secondaryColor)
  const secondaryColorUse = secondaryColor || primaryColor

  const uniteColorSet = (
    colorSet: Partial<MvhColorsType>,
    submitButtonColor: string,
    cancelButtonColor: string
  ): Partial<MvhColorsType> => ({
    ...colorSet,
    submitButtonColor,
    cancelButtonColor,
    submitButtonTextColor: blackOrWhiteText(submitButtonColor),
    cancelButtonTextColor: blackOrWhiteText(cancelButtonColor),
    regularFont: (secondaryFontUrl || primaryFontUrl) ?? '',
    boldFont: primaryFontUrl ?? ''
  })

  if (primaryColor && isDarkColor(primaryColor)) {
    // dark primary color - lighten other colors for buttons
    const cancelButtonColor = primaryColor && lighten(primaryColor, 0.4)
    const submitButtonColor =
      secondaryColorUse && isDarkColor(secondaryColorUse)
        ? lighten(secondaryColorUse, 0.4)
        : secondaryColorUse
    const titleColor = secondaryColorUse
      ? lighten(secondaryColorUse, 0.7)
      : '#fff'

    return uniteColorSet(
      {
        backgroundColor: primaryColor,
        placeholderColor: '#ccc',
        textColor: '#fff',
        titleColor
      },
      submitButtonColor,
      cancelButtonColor
    )
  }

  // light primary color - darken other colors for buttons
  const cancelButtonColor = primaryColor && darken(primaryColor, 0.4)
  const submitButtonColor =
    !secondaryColorUse || isDarkColor(secondaryColorUse)
      ? secondaryColorUse
      : darken(secondaryColorUse, 0.4)
  const titleColor = secondaryColorUse ? darken(secondaryColorUse, 0.7) : '#000'

  return uniteColorSet(
    {
      backgroundColor: primaryColor,
      placeholderColor: '#333',
      textColor: '#000',
      titleColor
    },
    submitButtonColor,
    cancelButtonColor
  )
}
